import React, { Fragment, useEffect, useState } from 'react'
import { Edit } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { GenericTableV2 } from '../../components/_EOS/GenericTable'
import { Modal } from '../../components/_EOS/Modal'
import { SnackBar } from '../../components/SnackBar/Snackbar'

import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Status } from '../../hooks/useFetchState'

import { filterOptions } from './config/filterOptions'
import { getColumnsConfig } from './config/tableColumns'
import { getProductOriginsTechnical } from './config/api'
import { CreateEditProductOriginTechnicalModal } from './modal/CreateEditProductOriginTechnicalModal'

import { ProductOrigin } from '../../interfaces/Producto/ProductOrigins'
import { GenericOrderBy } from '../../components/_EOS/Table'
import { ApiResponse } from '../../typings/ApiResponse'

export const ProductOriginTechnicalPage: React.FC = () => {
  const { callApi, state } = useApi<ApiResponse<ProductOrigin>>()
  const pagination = usePagination(5)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  const [orderBy, setOrderBy] = useState<GenericOrderBy<ProductOrigin>>({
    field: 'name' as keyof ProductOrigin,
    direction: 'asc'
  })

  const columnsConfig = getColumnsConfig()

  const createModal = useModal('Crear Nueva Marca')
  const editModal = useModal<ProductOrigin>('Editar Marca')

  const requestData = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    const filtersToUse = filters !== undefined ? filters : selectedFilters
    const pageToUse = page !== undefined ? page : pagination.page
    const pageSizeToUse =
      pageSize !== undefined ? pageSize : pagination.pageSize

    callApi(
      getProductOriginsTechnical(
        filtersToUse,
        pageToUse,
        pageSizeToUse,
        orderBy
      )
    ).catch((error) => {
      openSnackBar(error, false)
    })
  }

  const resetAndFetchData = (filters?: Filter[]) => {
    pagination.setPagination(0, pagination.pageSize)
    requestData(filters, 0, pagination.pageSize)
  }

  const handleSorting = (
    field: keyof ProductOrigin,
    direction: 'asc' | 'desc'
  ) => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleCreateClick = () => {
    createModal.openModal('md')
  }

  useEffect(() => {
    resetAndFetchData(selectedFilters)
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={resetAndFetchData}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeSearchOnClean={true}
          onAddButtonClick={handleCreateClick}
          createButtonTooltip='Crear Marca'
        />
        <GenericTableV2<ProductOrigin>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='productOriginId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof ProductOrigin,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('md', item),
              icon: <Edit />
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
        compact
        fillHeight='content'
      >
        <CreateEditProductOriginTechnicalModal
          isEditing={false}
          closeModal={createModal.closeModal}
          onSuccess={requestData}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
        compact
        fillHeight='content'
      >
        <CreateEditProductOriginTechnicalModal
          productOrigin={editModal.extendedProps}
          isEditing={true}
          closeModal={editModal.closeModal}
          onSuccess={requestData}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
